import { urlPilarHomes } from '~/constants';
import { defaultTwitterCardMeta } from '~/constants/seo';
import { normalizeString } from '~~/app/utils';

type GrammarGender = 'male' | 'female' | 'neutral';

type RegionMetadataGrammar = { gender: GrammarGender };

type RegionMetadataInput = {
	name: string;
	city: string;
	heroDescription?: string | null;
	heroImage?: string | null;
	fallbackImage?: string | null;
	grammar?: RegionMetadataGrammar;
};

const grammarPrefix: Record<GrammarGender, string> = {
	male: 'no',
	female: 'na',
	neutral: 'em'
};

function removeHtmlTags(value: string): string {
	return value
		.replace(/<[^>]*>/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
}

function getDefaultRegionImage(region: string): string {
	const imageBaseURL = '/images/social/openGraph/regions/';
	const normalizedRegion = normalizeString(region, [' ']).replaceAll(' ', '_');
	return `${urlPilarHomes}${imageBaseURL}${normalizedRegion}.jpg`;
}

function getAbsoluteImageURL(image: string | null | undefined): string {
	if (!image) return '';

	if (image.startsWith('http') || image.startsWith('www')) {
		return image;
	}

	const path = image.startsWith('/') ? image : `/${image}`;
	return `${urlPilarHomes}${path}`;
}

function getRegionDescription(input: RegionMetadataInput): string {
	const heroDescription = removeHtmlTags(input.heroDescription || '');

	if (heroDescription) {
		return `Como é morar e o que fazer em ${input.name}: ${heroDescription}`;
	}

	if (input.grammar) {
		const prefix = grammarPrefix[input.grammar.gender];
		return `Como é morar e o que fazer ${prefix} ${input.name}, bairro que combina vida pessoal e profissional, em umas das regiões mais privilegiadas de ${input.city}.`;
	}

	return `Como é morar e o que fazer em ${input.name}.`;
}

function normalizeRegionMetadataInput(
	region: string | RegionMetadataInput,
	city?: string,
	grammar?: RegionMetadataGrammar
): RegionMetadataInput {
	if (typeof region !== 'string') return region;

	return {
		name: region,
		city: city || '',
		grammar
	};
}

export function getRegionMetadata(
	region: string,
	city: string,
	grammar: RegionMetadataGrammar
): ReturnType<typeof createRegionMetadata>;
export function getRegionMetadata(
	input: RegionMetadataInput
): ReturnType<typeof createRegionMetadata>;
export function getRegionMetadata(
	region: string | RegionMetadataInput,
	city?: string,
	grammar?: RegionMetadataGrammar
) {
	const input = normalizeRegionMetadataInput(region, city, grammar);

	return createRegionMetadata(input);
}

function createRegionMetadata(input: RegionMetadataInput) {
	const title = `${input.name}, ${input.city} | Guia do Bairro | PilarHomes`;
	const description = getRegionDescription(input);
	const keywords = [input.name, input.city].filter(Boolean).join(', ');

	const imageURL =
		getAbsoluteImageURL(input.heroImage) ||
		getAbsoluteImageURL(input.fallbackImage) ||
		getDefaultRegionImage(input.name);

	return {
		title: title,
		description: description,
		keywords: keywords,
		ogTitle: title,
		ogDescription: description,
		ogImage: imageURL,
		twitterCard: defaultTwitterCardMeta,
		twitterTitle: title,
		twitterDescription: description,
		twitterImage: imageURL
	};
}
